import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Card,
  CardMedia,
  Radio,
  RadioGroup,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  PhotoCamera,
  Description,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { courseAPI, studentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

// Kerala districts
const KERALA_DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod'
];

const StudentEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isSuperAdminEdit = searchParams.get('superadmin') === 'true';
  const { user, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [student, setStudent] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    course: '',
    nameAsPerTC: '',
    dateOfBirth: '',
    gender: '',
    phone: '',
    email: '',
    permanentStreet: '',
    permanentCity: '',
    permanentDistrict: '',
    permanentState: 'Kerala',
    permanentPincode: '',
    communicationStreet: '',
    communicationCity: '',
    communicationDistrict: '',
    communicationState: 'Kerala',
    communicationPincode: '',
    fatherName: '',
    fatherOccupation: '',
    fatherAddress: '',
    fatherPhone: '',
    isEmployed: false,
    employerName: '',
    designation: '',
    employerAddress: '',
    applicantDeclarationAccepted: false,
    applicantDeclarationPlace: '',
    applicantDeclarationDate: new Date().toISOString().split('T')[0],
    parentDeclarationAccepted: false,
    parentDeclarationPlace: '',
    parentDeclarationDate: new Date().toISOString().split('T')[0],
  });

  const [qualifications, setQualifications] = useState([]);

  useEffect(() => {
    fetchCourses();
    fetchStudent();
  }, [id]);

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll();
      const activeCourses = (response.data.data || []).filter(c => c.isActive);
      setCourses(activeCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const fetchStudent = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getOne(id);
      const studentData = response.data.data.student;
      setStudent(studentData);

      // Check access
      if (!isSuperAdmin && studentData.status !== 'rejected') {
        toast.error('Only rejected applications can be edited');
        navigate('/admin/students');
        return;
      }

      // Populate form data
      setFormData({
        course: studentData.course?._id || '',
        nameAsPerTC: studentData.personalInfo?.nameAsPerTC || '',
        dateOfBirth: studentData.personalInfo?.dateOfBirth?.split('T')[0] || '',
        gender: studentData.personalInfo?.gender || '',
        phone: studentData.personalInfo?.phone || '',
        email: studentData.personalInfo?.email || '',
        permanentStreet: studentData.permanentAddress?.street || '',
        permanentCity: studentData.permanentAddress?.city || '',
        permanentDistrict: studentData.permanentAddress?.district || '',
        permanentState: studentData.permanentAddress?.state || 'Kerala',
        permanentPincode: studentData.permanentAddress?.pincode || '',
        communicationStreet: studentData.communicationAddress?.street || '',
        communicationCity: studentData.communicationAddress?.city || '',
        communicationDistrict: studentData.communicationAddress?.district || '',
        communicationState: studentData.communicationAddress?.state || 'Kerala',
        communicationPincode: studentData.communicationAddress?.pincode || '',
        fatherName: studentData.fatherOrGuardian?.name || '',
        fatherOccupation: studentData.fatherOrGuardian?.occupation || '',
        fatherAddress: studentData.fatherOrGuardian?.address || '',
        fatherPhone: studentData.fatherOrGuardian?.phone || '',
        isEmployed: studentData.employment?.isEmployed || false,
        employerName: studentData.employment?.employerName || '',
        designation: studentData.employment?.designation || '',
        employerAddress: studentData.employment?.employerAddress || '',
        applicantDeclarationAccepted: studentData.declarations?.applicantDeclaration?.accepted || false,
        applicantDeclarationPlace: studentData.declarations?.applicantDeclaration?.place || '',
        applicantDeclarationDate: studentData.declarations?.applicantDeclaration?.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        parentDeclarationAccepted: studentData.declarations?.parentGuardianDeclaration?.accepted || false,
        parentDeclarationPlace: studentData.declarations?.parentGuardianDeclaration?.place || '',
        parentDeclarationDate: studentData.declarations?.parentGuardianDeclaration?.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      });

      setQualifications(studentData.educationalQualifications || []);

      // Set photo preview if exists
      if (studentData.photo) {
        const photoUrl = studentData.photo.startsWith('uploads/')
          ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}/${studentData.photo}`
          : `${process.env.REACT_APP_API_URL?.replace('/api', '')}/uploads/students/${studentData.photo}`;
        setPhotoPreview(photoUrl);
      }

      // Set certificate preview if exists
      if (studentData.certificate) {
        if (!studentData.certificate.endsWith('.pdf')) {
          const certUrl = studentData.certificate.startsWith('uploads/')
            ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}/${studentData.certificate}`
            : `${process.env.REACT_APP_API_URL?.replace('/api', '')}/uploads/certificates/${studentData.certificate}`;
          setCertificatePreview(certUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching student:', error);
      toast.error('Failed to load student details');
      navigate('/admin/students');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSameAsPermanentChange = (e) => {
    const checked = e.target.checked;
    setSameAsPermanent(checked);

    if (checked) {
      setFormData((prev) => ({
        ...prev,
        communicationStreet: prev.permanentStreet,
        communicationCity: prev.permanentCity,
        communicationDistrict: prev.permanentDistrict,
        communicationState: prev.permanentState,
        communicationPincode: prev.permanentPincode,
      }));
    }
  };

  const handleQualificationChange = (index, field, value) => {
    const updatedQualifications = [...qualifications];
    updatedQualifications[index][field] = value;
    setQualifications(updatedQualifications);
  };

  const addQualification = () => {
    setQualifications([
      ...qualifications,
      {
        examination: '',
        universityOrBoard: '',
        yearOfPassing: '',
        classOrDivision: '',
        gradeOrPercentage: '',
      },
    ]);
  };

  const removeQualification = (index) => {
    const updatedQualifications = qualifications.filter((_, i) => i !== index);
    setQualifications(updatedQualifications);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select an image or PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Certificate size should be less than 10MB');
        return;
      }
      setCertificateFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCertificatePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setCertificatePreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.course || !formData.nameAsPerTC || !formData.dateOfBirth || !formData.gender || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError('Please enter a valid email address');
      toast.error('Please enter a valid email address');
      return;
    }

    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (!isSuperAdminEdit && (!formData.applicantDeclarationAccepted || !formData.parentDeclarationAccepted)) {
      toast.error('Please accept both declarations to proceed');
      return;
    }

    // Validate qualifications only if any exist
    if (qualifications.length > 0) {
      const incompleteQualification = qualifications.some(q =>
        !q.examination || !q.universityOrBoard || !q.yearOfPassing || !q.classOrDivision || !q.gradeOrPercentage
      );
      if (incompleteQualification) {
        toast.error('Please complete all educational qualification fields or remove incomplete entries');
        return;
      }
    }

    try {
      setSaving(true);

      const submitData = new FormData();

      submitData.append('course', formData.course);
      submitData.append('personalInfo', JSON.stringify({
        nameAsPerTC: formData.nameAsPerTC,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        phone: formData.phone,
        email: formData.email,
      }));
      submitData.append('permanentAddress', JSON.stringify({
        street: formData.permanentStreet,
        city: formData.permanentCity,
        district: formData.permanentDistrict,
        state: formData.permanentState,
        pincode: formData.permanentPincode,
      }));
      submitData.append('communicationAddress', JSON.stringify({
        street: formData.communicationStreet,
        city: formData.communicationCity,
        district: formData.communicationDistrict,
        state: formData.communicationState,
        pincode: formData.communicationPincode,
      }));
      submitData.append('fatherOrGuardian', JSON.stringify({
        name: formData.fatherName,
        occupation: formData.fatherOccupation,
        address: formData.fatherAddress,
        phone: formData.fatherPhone,
      }));

      const validQualifications = qualifications.filter(q =>
        q.examination && q.universityOrBoard && q.yearOfPassing && q.classOrDivision && q.gradeOrPercentage
      );
      submitData.append('educationalQualifications', JSON.stringify(validQualifications));

      submitData.append('employment', JSON.stringify({
        isEmployed: formData.isEmployed,
        employerName: formData.employerName,
        designation: formData.designation,
        employerAddress: formData.employerAddress,
      }));
      submitData.append('declarations', JSON.stringify({
        applicantDeclaration: {
          accepted: formData.applicantDeclarationAccepted,
          date: formData.applicantDeclarationDate,
          place: formData.applicantDeclarationPlace,
        },
        parentGuardianDeclaration: {
          accepted: formData.parentDeclarationAccepted,
          date: formData.parentDeclarationDate,
          place: formData.parentDeclarationPlace,
        },
      }));

      if (photoFile) {
        submitData.append('photo', photoFile);
      }
      if (certificateFile) {
        submitData.append('certificate', certificateFile);
      }

      // Use different API based on user role
      if (isSuperAdmin && isSuperAdminEdit) {
        await studentAPI.editBySuperAdmin(id, submitData);
        toast.success('Student details updated successfully');
      } else {
        await studentAPI.update(id, submitData);
        toast.success('Application updated and resubmitted successfully');
      }
      navigate('/admin/students');
    } catch (error) {
      console.error('Error updating application:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update application';
      if (errorMessage.toLowerCase().includes('email')) {
        setEmailError(errorMessage);
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isSuperAdminEdit ? 'Edit Student Details' : 'Edit & Resubmit Application'}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/students')}>
          Back to Students
        </Button>
      </Box>

      {student?.status === 'rejected' && student?.rejectionReason && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Previous Rejection Reason:</Typography>
          <Typography variant="body2">{student.rejectionReason}</Typography>
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        {isSuperAdminEdit
          ? 'You are editing this student as SuperAdmin. Changes will be saved without changing the status.'
          : 'Make necessary corrections and resubmit the application for approval.'}
      </Alert>

      <form onSubmit={handleSubmit}>
        {/* Course Selection */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Course Selection
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select Course</InputLabel>
                <Select
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  label="Select Course"
                >
                  {courses.map((course) => (
                    <MenuItem key={course._id} value={course._id}>
                      {course.name} ({course.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Personal Information */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Personal Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name (as per TC)"
                name="nameAsPerTC"
                value={formData.nameAsPerTC}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <FormLabel>Gender</FormLabel>
                <RadioGroup
                  row
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Male" control={<Radio />} label="Male" />
                  <FormControlLabel value="Female" control={<Radio />} label="Female" />
                  <FormControlLabel value="Other" control={<Radio />} label="Other" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  handleChange(e);
                  setEmailError('');
                }}
                required
                fullWidth
                error={!!emailError}
                helperText={emailError || 'Required for sending login credentials when approved. Must be unique.'}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Photo Upload */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Student Photo
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                fullWidth
              >
                {photoFile ? 'Change Photo' : 'Upload New Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max size: 5MB. Formats: JPG, PNG, GIF
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {photoPreview && (
                <Card sx={{ maxWidth: 200 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={photoPreview}
                    alt="Student Photo"
                  />
                </Card>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Certificate Upload */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Certificate
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Description />}
                fullWidth
                color={certificateFile ? 'success' : 'primary'}
              >
                {certificateFile ? 'Change Certificate' : 'Upload New Certificate'}
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={handleCertificateChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max size: 10MB. Formats: JPG, PNG, GIF, PDF
              </Typography>
              {certificateFile && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Selected: {certificateFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {certificatePreview && (
                <Card sx={{ maxWidth: 200 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={certificatePreview}
                    alt="Certificate Preview"
                  />
                </Card>
              )}
              {student?.certificate && !certificateFile && (
                <Alert severity="info">
                  Existing certificate: {student.certificate.split('/').pop()}
                </Alert>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Permanent Address */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Permanent Address
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Street/House Name"
                name="permanentStreet"
                value={formData.permanentStreet}
                onChange={handleChange}
                required
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                name="permanentCity"
                value={formData.permanentCity}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>District</InputLabel>
                <Select
                  label="District"
                  name="permanentDistrict"
                  value={formData.permanentDistrict}
                  onChange={handleChange}
                >
                  {KERALA_DISTRICTS.map((district) => (
                    <MenuItem key={district} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="State"
                name="permanentState"
                value={formData.permanentState}
                onChange={handleChange}
                required
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Pincode"
                name="permanentPincode"
                value={formData.permanentPincode}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Communication Address */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Communication Address
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sameAsPermanent}
                  onChange={handleSameAsPermanentChange}
                />
              }
              label="Same as Permanent Address"
            />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Street/House Name"
                name="communicationStreet"
                value={formData.communicationStreet}
                onChange={handleChange}
                required
                fullWidth
                multiline
                rows={2}
                disabled={sameAsPermanent}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                name="communicationCity"
                value={formData.communicationCity}
                onChange={handleChange}
                required
                fullWidth
                disabled={sameAsPermanent}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={sameAsPermanent}>
                <InputLabel>District</InputLabel>
                <Select
                  label="District"
                  name="communicationDistrict"
                  value={formData.communicationDistrict}
                  onChange={handleChange}
                >
                  {KERALA_DISTRICTS.map((district) => (
                    <MenuItem key={district} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="State"
                name="communicationState"
                value={formData.communicationState}
                onChange={handleChange}
                required
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Pincode"
                name="communicationPincode"
                value={formData.communicationPincode}
                onChange={handleChange}
                required
                fullWidth
                disabled={sameAsPermanent}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Father/Guardian Information */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Father/Guardian Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Father/Guardian Name"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Occupation"
                name="fatherOccupation"
                value={formData.fatherOccupation}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Address"
                name="fatherAddress"
                value={formData.fatherAddress}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number"
                name="fatherPhone"
                value={formData.fatherPhone}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Educational Qualifications */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Educational Qualifications (Optional)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={addQualification}
            >
              Add Qualification
            </Button>
          </Box>

          {qualifications.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Examination</TableCell>
                    <TableCell>University/Board</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Class/Division</TableCell>
                    <TableCell>Grade/%</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qualifications.map((qual, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.examination}
                          onChange={(e) => handleQualificationChange(index, 'examination', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.universityOrBoard}
                          onChange={(e) => handleQualificationChange(index, 'universityOrBoard', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={qual.yearOfPassing}
                          onChange={(e) => handleQualificationChange(index, 'yearOfPassing', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.classOrDivision}
                          onChange={(e) => handleQualificationChange(index, 'classOrDivision', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={qual.gradeOrPercentage}
                          onChange={(e) => handleQualificationChange(index, 'gradeOrPercentage', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeQualification(index)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No educational qualifications added. Click "Add Qualification" to add your educational background.
            </Alert>
          )}
        </Paper>

        {/* Employment Status */}
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Employment Status
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isEmployed"
                    checked={formData.isEmployed}
                    onChange={handleChange}
                  />
                }
                label="Currently Employed"
              />
            </Grid>
            {formData.isEmployed && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Employer Name"
                    name="employerName"
                    value={formData.employerName}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Employer Address"
                    name="employerAddress"
                    value={formData.employerAddress}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Declarations */}
        {!isSuperAdminEdit && (
          <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Declarations
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Applicant's Declaration:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                I hereby declare that the information given above is true to the best of my knowledge and belief.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="applicantDeclarationAccepted"
                        checked={formData.applicantDeclarationAccepted}
                        onChange={handleChange}
                        required
                      />
                    }
                    label="I accept the above declaration"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Place"
                    name="applicantDeclarationPlace"
                    value={formData.applicantDeclarationPlace}
                    onChange={handleChange}
                    required={formData.applicantDeclarationAccepted}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Date"
                    name="applicantDeclarationDate"
                    type="date"
                    value={formData.applicantDeclarationDate}
                    onChange={handleChange}
                    required={formData.applicantDeclarationAccepted}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Parent/Guardian's Declaration:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                I hereby give my consent for my son/daughter/ward to join this course and declare that the above information is correct.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="parentDeclarationAccepted"
                        checked={formData.parentDeclarationAccepted}
                        onChange={handleChange}
                        required
                      />
                    }
                    label="I accept the above declaration"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Place"
                    name="parentDeclarationPlace"
                    value={formData.parentDeclarationPlace}
                    onChange={handleChange}
                    required={formData.parentDeclarationAccepted}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Date"
                    name="parentDeclarationDate"
                    type="date"
                    value={formData.parentDeclarationDate}
                    onChange={handleChange}
                    required={formData.parentDeclarationAccepted}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/students')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={saving}
          >
            {saving ? 'Saving...' : (isSuperAdminEdit ? 'Save Changes' : 'Resubmit Application')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default StudentEdit;
